import React, { useEffect } from 'react';
import { PageSparkleLayer } from './components/SparkleHeaderFx';
import { FiExternalLink, FiFacebook, FiInstagram, FiYoutube } from 'react-icons/fi';
import { SiTiktok } from 'react-icons/si';
import SiteNav from './components/SiteNav';
import HeroSlider from './components/HeroSlider';
import CtaTiles from './components/CtaTiles';
import Reveal from './components/Reveal';
import CharacterCarousel from './components/CharacterCarousel';
import NewsletterSignup from './components/NewsletterSignup';
import AmazonBooksCarousel from './components/AmazonBooksCarousel';
import useNavSolid from './hooks/useNavSolid';
import { BUY_LABEL, COMING_SOON_LABEL, STATUS, getBooksForPillar } from './data/library';
import { SITE_HOME_URL, SHOW_VIDEOS } from './config';
import videoThumbPlaceholder from './assets/video-thumb-placeholder.svg';
import footerBg from './assets/footer-bg.png';

const PLACEHOLDER_SOCIAL_BASE = 'https://example.org';

const SOCIAL_LINKS = [
  { href: `https://www.facebook.com/profile.php?id=61560690730958`, label: 'Facebook', Icon: FiFacebook },
  { href: `${PLACEHOLDER_SOCIAL_BASE}/libby/youtube`, label: 'YouTube', Icon: FiYoutube },
  { href: `${PLACEHOLDER_SOCIAL_BASE}/libby/instagram`, label: 'Instagram', Icon: FiInstagram },
  { href: `${PLACEHOLDER_SOCIAL_BASE}/libby/tiktok`, label: 'TikTok', Icon: SiTiktok },
];

const MERCH_ITEMS = ['Plushies', 'Lunchbox Kit', 'Stationary', 'Backpack', 'T-Shirts', 'And more...'];

const VIDEO_CHANNELS = [
  {
    id: 'youtube',
    label: 'YouTube',
    href: `${PLACEHOLDER_SOCIAL_BASE}/libby/youtube-channel`,
    Icon: FiYoutube,
    thumbAlt: 'YouTube — videos arriving soon',
  },
  {
    id: 'tiktok',
    label: 'TikTok',
    href: `${PLACEHOLDER_SOCIAL_BASE}/libby/tiktok`,
    Icon: SiTiktok,
    thumbAlt: 'TikTok — videos arriving soon',
  },
];

export default function App() {
  const navSolid = useNavSolid(64);
  const year = new Date().getFullYear();

  useEffect(() => {
    const canonical = SITE_HOME_URL;
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', canonical);
    document.querySelector('meta[property="og:url"]')?.setAttribute('content', canonical);
  }, []);

  return (
    <div className="v2-shell">
      <SiteNav solid={navSolid} />
      <HeroSlider />

      <main className="v2-main">
        <div className="page-sparkle-field" aria-hidden>
          <PageSparkleLayer />
        </div>
        <section id="foundation" className="foundation">
          <div className="foundation__inner">
            <Reveal>
              <p className="section-eyebrow">Our foundation</p>
              <h2 className="section-display">Stories kids can cheer for</h2>
            </Reveal>
            <Reveal delay={60}>
              <div className="foundation__intro">
                <p className="foundation__lead">
                  Libby the Lion &amp; Friends celebrates kindness, curiosity, and confidence through books,
                  activities, and characters kids can cheer for. We are building a gentle, joyful world where every
                  letter, lesson, and laugh feels like an adventure with friends.
                </p>
                <p className="foundation__muted">
                  Meet the friends, explore books and videos, and sign up for updates — a joyful world for little fans
                  and grown-ups who love reading together.
                </p>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <p className="foundation__carousel-label">Meet the friends</p>
              <CharacterCarousel />
            </Reveal>
          </div>
        </section>

        <section id="explore" className="cta-band" aria-labelledby="explore-heading">
          <div className="cta-band__inner">
            <Reveal>
              <h2 id="explore-heading" className="section-display section-display--light">
                Explore Libby&apos;s world
              </h2>
              <p className="cta-band__sub">Jump to what matters most — books, merch, and ways to stay in touch.</p>
            </Reveal>
            <Reveal delay={100}>
              <CtaTiles />
            </Reveal>
          </div>
        </section>

        <section id="amazon" className="amazon-band" aria-labelledby="amazon-heading">
          <div className="amazon-band__inner">
            <Reveal>
              <h2 id="amazon-heading" className="visually-hidden">
                Buy now
              </h2>
              <AmazonBooksCarousel />
            </Reveal>
          </div>
        </section>

        <section id="discover" className="pillars" aria-labelledby="pillars-heading">
          <div className="pillars__inner">
            <Reveal>
              <span className="section-badge">Discover</span>
              <h2 id="pillars-heading" className="section-display">
                Stories &amp; more{SHOW_VIDEOS ? ' & videos' : ''}
              </h2>
            </Reveal>
            <div className="pillars__grid">
              <Reveal className="pillar-card" delay={0}>
                <h3 className="pillar-card__title">Books</h3>
                <p className="pillar-card__kicker">On the shelf &amp; on the way</p>
                <div className="pillar-card__body-wrap">
                  <ul className="pillar-card__list pillar-card__book-list">
                    {getBooksForPillar().map(book => (
                        <li key={book.id} className="pillar-book-item">
                          <span className="pillar-book-item__title">{book.title}</span>
                          {book.status === STATUS.BUY_NOW ? (
                            <a
                              href={book.buyUrl}
                              className="btn-buy-amazon btn-buy-amazon--compact"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {BUY_LABEL}
                              <FiExternalLink size={16} aria-hidden />
                            </a>
                          ) : (
                            <span className="pillar-book-item__badge">{COMING_SOON_LABEL}</span>
                          )}
                        </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
              <Reveal className="pillar-card" delay={80}>
                <h3 className="pillar-card__title">Merchandise</h3>
                <p className="pillar-card__kicker">Cozy gear &amp; goodies</p>
                <div className="pillar-card__body-wrap">
                  <ul className="pillar-card__tags pillar-card__merch-list">
                    {MERCH_ITEMS.map(item => (
                      <li key={item} className="pillar-merch-item">
                        <span className="merch-pill">{item}</span>
                        <span className="pillar-book-item__badge">Coming soon</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
              {SHOW_VIDEOS && (
                <Reveal className="pillar-card pillar-card--span-2" delay={160}>
                  <h3 className="pillar-card__title">Videos</h3>
                  <p className="pillar-card__kicker">Short episodes &amp; clips — arriving soon</p>
                  <div className="pillar-card__body-wrap">
                    <ul className="pillar-card__videos">
                      {VIDEO_CHANNELS.map(({ id, label, href, Icon, thumbAlt }) => (
                        <li key={id}>
                          <a
                            href={href}
                            className={`video-thumb-tile video-thumb-tile--${id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <span className="video-thumb-tile__media">
                              <img src={videoThumbPlaceholder} alt="" className="video-thumb-tile__img" decoding="async" />
                              <span className="video-thumb-tile__play" aria-hidden>
                                <span className="video-thumb-tile__play-icon" />
                              </span>
                            </span>
                            <span className="video-thumb-tile__label">
                              <Icon size={18} aria-hidden />
                              <span>{label}</span>
                              <span className="video-thumb-tile__badge">Arriving soon</span>
                            </span>
                            <span className="visually-hidden">{thumbAlt}</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              )}
            </div>
          </div>
        </section>

        <section className="newsletter-band">
          <div className="newsletter-band__inner">
            <Reveal>
              <NewsletterSignup />
            </Reveal>
          </div>
        </section>

        <section id="social" className="social-band" aria-labelledby="social-heading">
          <div className="social-band__inner">
            <Reveal>
              <h2 id="social-heading" className="section-display section-display--light">
                Follow along
              </h2>
              <div className="social-grid">
                {SOCIAL_LINKS.map(({ href, label, Icon }) => (
                  <a key={label} href={href} className="social-tile" target="_blank" rel="noopener noreferrer">
                    <Icon size={22} aria-hidden />
                    <span>{label}</span>
                  </a>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      <footer className="v2-footer">
        <div className="footer-title-band">
          <p className="footer-line">Thanks for visiting</p>
          <p className="footer-trademark">Trademark - Nvested Realty · © {year}</p>
        </div>
        <div className="soft-footer" aria-hidden="true">
          <img src={footerBg} alt="" width={1024} height={341} decoding="async" />
        </div>
      </footer>
    </div>
  );
}
